// CLEARANCE: old stock, sealed and never installed (Oct 2026).
//
// One entry per unit model. This file is the single list that the clearance
// page, each unit's own page, the cart, the payment server and the Google
// Merchant feed all read. Old stock only: current-year models do not belong
// here (the 2026 O-General ASGG18CKAA-B and ASGA14BMAA-B are held but are not
// clearance).
//
// PRICE AND STOCK ARE NOT HERE. They live in src/data/pricing.js with every
// other unit, in PRICES and STOCK under the same id, so the stock count moves
// by itself when a unit sells online, exactly as it does for Daikin:
//   above 0    in stock: its page has Add to cart and Buy now
//   exactly 0  SOLD OUT: the card stays on the page with the red "Sold out"
//              pill, last in the list, and its page says Sold out
//   no entry   treated as sold out
// Delete a unit from this list when it is no longer worth showing.
//
// PRICE RULE: purchase price + 600, rounded up to the next 500, GST inclusive.
//
// ADDING A UNIT: add an entry here (id starts "cl-", model code exactly as on
// the box), its price in PRICES and its count in STOCK under the same id and
// kit, and its 1000x750 photo on white in public/clearance/.
//
// SPECS come only from the sources noted on each entry. A unit with no
// verified source shows no spec line rather than a guess.
//   CKA  O-General India catalogue: general-hvac.com/shared/in/pdf-gcin-support-ctlg-cka-series-2025-01.pdf
//   CPAA Croma listing for ASGG18CPAA-B (specifications table)
//   JLCA O-General Asia catalogue 2017-18 (ASGG12JLCA)
//   B222 Mahajan Electronics listing for RAS.B222PCAIBA1
// Photos: the O-General CKA catalogue (CKA series picture, used for every CKA
// unit), the O-General 2017-18 catalogue (JLCA), and the photos Saheb supplied
// for CPAA, both Hitachis and the Daikin (Oct 2026).
//
// kit: 'withKit' when the copper piping kit is in the box, else 'withoutKit'.
// It is the key used in PRICES and STOCK.

export const CLEARANCE_PATH = 'clearance-ac-sale';

export const BRAND_LOGO = {
  'O-General': '/brands/o-general.webp',
  Hitachi: '/brands/hitachi.webp',
  Daikin: '/brands/daikin.webp',
};

const UNITS = [
  // CPAA: 5,280 W, ISEER 3.83, R32, copper, PM 2.5 + mildew filters, 55 C.
  { id: 'cl-asgg18cpaa-b', brand: 'O-General', code: 'ASGG18CPAA-B', name: '1.5 Ton 3 Star Inverter Split AC',
    soldAs: '1.5', stars: 3, inverter: true, year: 2023, kit: 'withoutKit', img: '/clearance/asgg18cpaa-b.webp',
    kw: 5.28, iseer: 3.83, refrigerant: 'R32', coil: 'Copper', maxTemp: 55,
    feature: 'PM 2.5 filter', featureNote: 'Plus anti-mildew filter' },
  // CKA: 5,280 W, ISEER 3.83, R32, copper coils, 18-55 C, 155-265 V. No piping kit in the box.
  { id: 'cl-asgg18ckaa-b', brand: 'O-General', code: 'ASGG18CKAA-B', name: '1.5 Ton 3 Star Inverter Split AC',
    soldAs: '1.5', stars: 3, inverter: true, year: 2023, kit: 'withoutKit', img: '/clearance/asgg18ckaa-b.webp',
    kw: 5.28, iseer: 3.83, refrigerant: 'R32', coil: 'Copper', maxTemp: 55,
    feature: 'Runs on 155–265 V', featureShort: '155–265 V', featureNote: 'Copes with Delhi voltage swings' },
  // JLCA: 3.5 kW (11,900 Btu/h), inverter, R410A, cooling 18-46 C. 4 star per Saheb's sheet.
  { id: 'cl-asgg12jlcab', brand: 'O-General', code: 'ASGG12JLCAB', name: '1 Ton 4 Star Inverter Split AC',
    soldAs: '1', stars: 4, inverter: true, year: 2018, kit: 'withoutKit', img: '/clearance/asgg12jlcab.webp',
    kw: 3.5, refrigerant: 'R410A', maxTemp: 46 },
  // CKA: 6,450 W (1.834 TR), ISEER 4.24.
  { id: 'cl-asgg22ckaa-b', brand: 'O-General', code: 'ASGG22CKAA-B', name: '1.8 Ton 3 Star Inverter Split AC',
    soldAs: '1.8', stars: 3, inverter: true, year: 2023, kit: 'withoutKit', img: '/clearance/asgg18ckaa-b.webp',
    kw: 6.45, iseer: 4.24, refrigerant: 'R32', coil: 'Copper', maxTemp: 55 },
  // No verified spec source yet for this exact code, so no spec line.
  { id: 'cl-ras-e518pccib1', brand: 'Hitachi', code: 'RAS.E518PCCIB1', name: '1.5 Ton 5 Star Inverter Split AC',
    soldAs: '1.5', stars: 5, inverter: true, year: 2024, kit: 'withKit', img: '/clearance/ras.e518pccib1.webp' },
  // B222: 21,665 Btu/h (1.8 TR), 2 star, R32, 100% copper, air throw up to 24 m, rated to 52 C.
  { id: 'cl-ras-b222pcaiba1', brand: 'Hitachi', code: 'RAS.B222PCAIBA1', name: '1.8 Ton 2 Star Fixed Speed Split AC',
    soldAs: '1.8', stars: 2, inverter: false, year: 2024, kit: 'withKit', img: '/clearance/ras.b222pcaiba1.webp',
    kw: 6.35, refrigerant: 'R32', coil: 'Copper', maxTemp: 52,
    feature: 'Air throw up to 24 m', featureShort: '24 m air throw', featureNote: 'Reaches the far end of long rooms' },
  // No verified spec source yet, so no spec line.
  { id: 'cl-gtky35uv16w3', brand: 'Daikin', code: 'GTKY35UV16W3', name: '1 Ton 3 Star Split AC',
    soldAs: '1', stars: 3, year: 2024, kit: 'withoutKit', img: '/clearance/gtky35uv16w3.webp' },
];

const slugify = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

// Shaped like a CATALOGUE model where the cart and checkout read one (id,
// title, image, slug, soldAs, kw, variants[kit].idu), so the same cart,
// checkout and payment server sell both. mrp is null: no MRP is claimed.
//
// STAR RATING ON OLD STOCK (Oct 2026, to rule out any mis-selling claim): the
// stars on an old unit are the BEE rating on its own label, given under the
// BEE norms in force in its manufacturing year (`year` below). BEE has made
// star norms stricter since, so under today's norms the same unit may get
// fewer stars. On screen (cards, unit pages) the star count carries an
// asterisk, "3 Star*", with a plain footnote at the bottom of the page
// (displayName / displayTitle + starNote). Where no footnote can follow (cart,
// checkout, order emails, quote PDF, WhatsApp messages, page titles and the
// Merchant feed) the year is written into the name instead: name / title /
// fullTitle read "3 Star (2023 BEE norms)". `year` must be the manufacturing
// year printed on the box / BEE label.
export const starQualifier = (u) => `${u.stars} Star (${u.year} BEE norms)`;
export const starNote = (u) =>
  `*Star rating as published by the manufacturer, as per BEE norms in force in the unit's manufacturing year (${u.year}). ` +
  'Current BEE norms are stricter, so the same unit may get fewer stars today.';
// Warranty wording for old stock (Oct 2026): never "full manufacturer warranty".
export const WARRANTY_TEXT = 'Warranty as per OEM standards for the product type';

export const STAR_NOTE_ALL =
  '*Star ratings as published by the manufacturer, as per BEE norms in force in each unit\'s manufacturing year. ' +
  'Current BEE norms are stricter, so the same unit may get fewer stars today.';

export const CLEARANCE = UNITS.map((u) => {
  if (!u.year || !u.stars) throw new Error(`${u.id}: clearance units need both year and stars.`);
  const name = u.name.replace(/\b\d Star\b/, starQualifier(u));
  if (name === u.name) throw new Error(`${u.id}: name must contain "<n> Star" so its BEE year can be shown.`);
  const title = `${u.brand} ${name}`;
  const displayName = u.name.replace(/\b(\d Star)\b/, '$1*');
  const specs = [
    u.kw && `${u.kw} kW`,
    u.iseer && `ISEER ${u.iseer}`,
    u.refrigerant,
    u.coil && `${u.coil} coil`,
  ].filter(Boolean);
  return {
    ...u,
    name,
    clearance: true,
    displayName,
    displayTitle: `${u.brand} ${displayName}`,
    displayFullTitle: `${u.brand} ${displayName} (${u.code})`,
    starLabel: starQualifier(u),
    starNote: starNote(u),
    title,
    fullTitle: `${title} (${u.code})`,
    slug: `${CLEARANCE_PATH}/${slugify(`${u.brand}-${u.code}`)}`,
    image: u.img,
    specs,
    variants: { [u.kit]: { idu: u.code, odu: '', mrp: null } },
  };
});

export const findClearance = (id) => CLEARANCE.find((u) => u.id === id) || null;
export const findClearanceBySlug = (slug) =>
  CLEARANCE.find((u) => u.slug === String(slug || '').replace(/^\/+|\/+$/g, '')) || null;
